import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';

import setNullEmptyValues from 'common/util/setNullEmptyValues';

import {
  APARTMENT_YARD_SIGN_REDIRECTION_UTM_QUERY_PARAMETERS,
} from 'common/apartmentHelpers';

import {
  SR_API_ACTION_APARTMENT_UPSERT,
  SR_API_ACTION_APARTMENT_GET_REDIRECTION,
  SR_API_ACTION_GET_REDIRECTION,
  SR_API_ACTION_SET_REDIRECTION,
  SR_API_ACTION_DELETE_REDIRECTION,
} from 'api/actionTypes';
import log from 'client/lib/log';
import {
  createInitialValues,
  createFormValidationFn,
  hideFieldIf,
} from 'client/helpers/formikHelpers';

import ButtonDeprecated from 'client/components/ButtonDeprecated';
import SrFormikField, {
  SR_FIELD_THEME_ADMIN,
} from 'client/components/SrFormikField';

import postWithState from 'client/actions/postWithState';
import getListingById from 'client/actions/Listing/getById';
import getApartmentById from 'client/actions/Apartment/getById';

function getApartmentFormFields({ apartment, partnersList }) {
  return [
    hideFieldIf(apartment.id, {
      id: 'partnerId',
      type: 'select',
      title: 'partner',
      properties: {
        choices: partnersList.map((partner) => ({
          label: partner.displayName,
          value: partner.id,
        })),
        transformToNumber: true,
      },
      validations: ['required'],
    }),
    {
      id: 'name',
      type: 'short_text',
      title: 'name',
      validations: ['required'],
    },
    hideFieldIf(apartment.id, {
      id: 'addressZip',
      type: 'short_text',
      title: 'zip',
      validations: ['required', 'zipCode'],
    }),
    {
      id: 'numberOfUnits',
      type: 'number',
      title: 'units',
      validations: ['required', 'positiveNumber'],
    },
    {
      id: 'yardSignUrl',
      title: 'yard sign url',
      type: 'short_text',
    },
    {
      id: 'jobSetupInstructions',
      type: 'long_text',
      title: 'unit setup instructions',
    },
    {
      id: 'lockboxCode',
      type: 'short_text',
      title: 'lockbox code',
    },
    {
      id: 'lockboxPhotoUrl',
      type: 'file_single',
      title: 'lockbox photo url',
      properties: {
        fileAccept: '*',
        filePath: '/apartment-uploads/lockbox-photos/{uuid}',
      },
    },
    {
      id: 'lockboxInstructions',
      type: 'long_text',
      title: 'lockbox instructions',
    },
  ];
}

class SrApartmentForm extends Component {
  static propTypes = {
    apartment: PropTypes.object.isRequired,
    partnersList: PropTypes.array,
    onChange: PropTypes.func,
    dispatch: PropTypes.func.isRequired,
  };

  static defaultProps = {
    partnersList: [],
  }

  constructor() {
    super();

    this.state = {
      redirectionUrlLoaded: false,
      redirectionUrlId: null,
    };
  }

  componentDidMount() {
    const { dispatch, apartment } = this.props;

    dispatch(postWithState, {
      action: SR_API_ACTION_APARTMENT_GET_REDIRECTION,
      payload: {
        apartmentId: apartment.id,
      },
    }).then((redirectionUrl) => {
      this.setState({
        redirectionUrlLoaded: true,
        redirectionUrlId: redirectionUrl?.id || null,
      });
    });
  }

  _onSubmitForm = async (formValues) => {
    try {
      const { apartment, onChange, dispatch } = this.props;
      const { redirectionUrlId } = this.state;
      const newYardSignUrl = formValues.yardSignUrl || null;

      if (newYardSignUrl && newYardSignUrl !== redirectionUrlId) {
        const existingYardSignUrl = await dispatch(postWithState, {
          action: SR_API_ACTION_GET_REDIRECTION,
          payload: {
            id: newYardSignUrl,
          },
        });
        if (existingYardSignUrl) {
          if (existingYardSignUrl.includes('listing/')) {
            const listingIdStr = existingYardSignUrl
              .substr(existingYardSignUrl.indexOf('listing/'))
              .split('/')[1];
            const listingId = parseInt(listingIdStr, 10);
            const listing = await dispatch(getListingById, listingId);

            if (listing) {
              const confirmMessage = (
                `This url is already in use by ${listing.addressStreetDisplay} - ${listing.id}. ` +
                `The status is ${listing.status}. Are you sure you want to override the url?`
              );

              if (!global.confirm(confirmMessage)) {
                return;
              }
            }
          }
          else if (existingYardSignUrl.includes('apartment/')) {
            const apartmentIdStr = existingYardSignUrl
              .substr(existingYardSignUrl.indexOf('apartment/'))
              .split('/')[1];
            const apartmentId = parseInt(apartmentIdStr, 10);
            const apartment = await dispatch(getApartmentById, { apartmentId });

            if (apartment) {
              const confirmMessage = (
                `This url is already in use by ${apartment.name} - ${apartment.id}. ` +
                'Are you sure you want to override the url?'
              );
              if (!global.confirm(confirmMessage)) {
                return;
              }
            }
          }
          else {
            global.alert(
              `This url is already redirecting to the url ${existingYardSignUrl}. ` +
              'Please check with the team before removing the current redirect.'
            );
            return;
          }
        }
      }

      delete formValues.yardSignUrl;

      const apartmentUpsertResult = await dispatch(postWithState, {
        action: SR_API_ACTION_APARTMENT_UPSERT,
        payload: {
          id: apartment.id,
          ...setNullEmptyValues(formValues),
        },
      });

      if (redirectionUrlId && redirectionUrlId !== newYardSignUrl) {
        await dispatch(postWithState, {
          action: SR_API_ACTION_DELETE_REDIRECTION,
          payload: {
            id: redirectionUrlId,
          },
        });
      }

      if (newYardSignUrl && newYardSignUrl !== redirectionUrlId) {
        await dispatch(postWithState, {
          action: SR_API_ACTION_SET_REDIRECTION,
          payload: {
            id: newYardSignUrl,
            url: (
              `/apartment/${apartmentUpsertResult.id}?${APARTMENT_YARD_SIGN_REDIRECTION_UTM_QUERY_PARAMETERS}`
            ),
          },
        });
      }
      if (onChange) {
        onChange();
      }
    }
    catch (error) {
      log.error(error);
    }
  }

  render() {
    const { apartment, partnersList } = this.props;
    const { redirectionUrlLoaded, redirectionUrlId } = this.state;
    const apartmentFormFields = getApartmentFormFields({ apartment, partnersList });

    if (!redirectionUrlLoaded) {
      return null;
    }

    return (
      <div className='SrApartmentForm'>
        <Formik
          initialValues={createInitialValues({
            formFields: apartmentFormFields,
            defaultValues: {
              ...apartment,
              yardSignUrl: redirectionUrlId,
            },
          })}
          validate={createFormValidationFn(apartmentFormFields)}
          onSubmit={this._onSubmitForm}
          render={(formikBag) => {
            return (
              <Form autoComplete='off'>
                {apartmentFormFields.map((formField) => (
                  <SrFormikField
                    key={formField.id}
                    formField={formField}
                    formikBag={formikBag}
                    className={SR_FIELD_THEME_ADMIN}
                  />
                ))}

                <ButtonDeprecated
                  type='submit'
                  disabled={!formikBag.isValid}
                >
                  {apartment.id ? 'update' : 'add'}
                </ButtonDeprecated>
              </Form>
            );
          }}
        />
      </div>
    );
  }
}

SrApartmentForm.displayName = 'SrApartmentForm';

export default SrApartmentForm;
